import { ExceptionReasonCodeStartsWith, Rule } from '../enums/DeliveryEnum';
import {ExceptionDictionaryType} from "../pages/delivery/parcels/graphql/queries/getAllException";
import {SiteType} from "../graphql/types/SiteType";

/**
 * 定义角色与异常码前缀的映射关系(总部无需过滤)
 */
export const roleToPrefixMap: Record<string, string[]> = {
    [Rule.DELIVERY_DRIVER]: [ExceptionReasonCodeStartsWith.ERDL],
    [Rule.DSP_MANAGER]: [
        ExceptionReasonCodeStartsWith.ERDSP
    ],
    [Rule.DSP_OWNER_TEAM]: [
        ExceptionReasonCodeStartsWith.ERDSP
    ],
    [Rule.HEADQUARTERS_STAFF]: [],
    'HEADQUARTERS_ADMIN': [], // 总部管理员与总部员工一致
    [Rule.WAREHOUSE_MANAGER]: [ExceptionReasonCodeStartsWith.ERWHS],
    [Rule.HUB_MANAGER]: [ExceptionReasonCodeStartsWith.ERHUB]
};

// 仅 PDA 可提报的异常码，后台标记时订单必然存在，不可选（ERHUB013 无订单：仅用于查无订单的件）
export const PDA_ONLY_EXCEPTION_CODES = ['ERHUB013'];

/**
 * 根据用户角色过滤异常数据
 *
 * @param exceptionList 异常列表数据
 * @param userRole 当前用户角色
 * @param siteType 站点类型
 * @returns 过滤后的异常数据列表
 */
export function filterExceptionByUserRole(
    exceptionList: ExceptionDictionaryType[],
    userRole: string,
    siteType: SiteType | undefined
): ExceptionDictionaryType[] {
    if (!exceptionList.length) return [];

    // 内部异常（isInternalException=true，如 QC 扣罚数字码）仅系统写入，任何角色的下拉都不可人工选择
    exceptionList = exceptionList.filter(item => !item.isInternalException);
    exceptionList = exceptionList.filter(item => !PDA_ONLY_EXCEPTION_CODES.includes(item.exceptionReasonCode ?? ''));

    let prefixes = roleToPrefixMap[userRole];

    // 总部员工和总部管理员根据 siteType 决定前缀
    if (userRole === Rule.HEADQUARTERS_STAFF || userRole === 'HEADQUARTERS_ADMIN') {
        if (siteType === SiteType.HUB) {
            prefixes = roleToPrefixMap[Rule.HUB_MANAGER];
        } else if (siteType === SiteType.WAREHOUSE) {
            prefixes = roleToPrefixMap[Rule.WAREHOUSE_MANAGER];
        } else {
            // 无 siteType 时，返回全部但需要排除被屏蔽的异常
            // 排除 warehouse 屏蔽的四个：ERWHS001、ERWHS003、ERWHS004、ERWHS005
            // 排除 dsp 屏蔽的两个：ERDSP001、ERDSP003
            return exceptionList.filter(item => {
                if (!item.exceptionReasonCode) return false;
                const blockedCodes = ['ERWHS001', 'ERWHS003', 'ERWHS004', 'ERWHS005', 'ERDSP001', 'ERDSP003'];
                return !blockedCodes.includes(item.exceptionReasonCode);
            });
        }
    }
    
    // 无前缀配置返回全部
    if (!prefixes) {
        return [...exceptionList];
    }

    return exceptionList.filter(item => {
        if (!item.exceptionReasonCode) return false;
        
        // 检查是否匹配前缀
        const matchesPrefix = prefixes.some(prefix => item.exceptionReasonCode?.startsWith(prefix));
        if (!matchesPrefix) return false;
        
        // DELIVERY_DRIVER 角色需要排除 ERDL005
        if (userRole === Rule.DELIVERY_DRIVER && item.exceptionReasonCode === 'ERDL005') {
            return false;
        }
        
        // DSP_MANAGER / DSP_OWNER_TEAM 角色需要排除 ERDSP001 和 ERDSP003
        if ((userRole === Rule.DSP_MANAGER || userRole === Rule.DSP_OWNER_TEAM) &&
            (item.exceptionReasonCode === 'ERDSP001' || item.exceptionReasonCode === 'ERDSP003')) {
            return false;
        }
        
        // WAREHOUSE_MANAGER 角色或总部员工/管理员在warehouse站点时，需要排除 ERWHS001、ERWHS003、ERWHS004、ERWHS005
        const isWarehouseContext = userRole === Rule.WAREHOUSE_MANAGER || 
            ((userRole === Rule.HEADQUARTERS_STAFF || userRole === 'HEADQUARTERS_ADMIN') && siteType === SiteType.WAREHOUSE);
        if (isWarehouseContext && 
            (item.exceptionReasonCode === 'ERWHS001' || 
             item.exceptionReasonCode === 'ERWHS003' || 
             item.exceptionReasonCode === 'ERWHS004' || 
             item.exceptionReasonCode === 'ERWHS005')) {
            return false;
        }
        
        // 总部员工和管理员在所有情况下都需要排除 DSP 屏蔽的两个异常
        if ((userRole === Rule.HEADQUARTERS_STAFF || userRole === 'HEADQUARTERS_ADMIN') && 
            (item.exceptionReasonCode === 'ERDSP001' || item.exceptionReasonCode === 'ERDSP003')) {
            return false;
        }
        
        return true;
    });
}
